import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ConsultaService } from 'src/app/service/consulta/consulta.service';
import { criarMonitoramentoConciliacaoForm } from './monitoramento-conciliacao-form.builder';
import { MonitoramentoConciliacaoRequest, mapMonitoramentoFormToRequest } from './monitoramento-conciliacao-form.dto';
import { MESES_REFERENCIA, MesReferenciaOption, gerarAnosReferencia } from './monitoramento-conciliacao-form.options';

@Component({
  selector: 'app-monitoramento-conciliacao-form',
  templateUrl: './monitoramento-conciliacao-form.component.html',
  styleUrls: [
    '../restituicao-form/restituicao-form.component.css',
    './monitoramento-conciliacao-form.component.css'
  ]
})
export class MonitoramentoConciliacaoFormComponent implements OnInit, OnDestroy {
  @Input() visualizacao = false;

  readonly maxObservacoes = 5000;

  form: FormGroup;
  anos: number[] = gerarAnosReferencia();
  meses: MesReferenciaOption[] = MESES_REFERENCIA;

  private subscriptions: Subscription[] = [];
  private sincronizandoConsulta = false;

  constructor(
    private fb: FormBuilder,
    public consultaService: ConsultaService
  ) {
    this.form = criarMonitoramentoConciliacaoForm(this.fb);
  }

  get observacoesControl(): AbstractControl {
    return this.consultaService.consulta.get('descricaoConsulta');
  }

  get quantidadeCaracteresRestantes(): number {
    const valor = this.observacoesControl ? this.observacoesControl.value : null;
    return this.maxObservacoes - (valor ? valor.length : 0);
  }

  private get monitoramentoControl(): AbstractControl {
    return this.consultaService.consulta.get('monitoramentoConciliacao');
  }

  ngOnInit(): void {
    const control = this.monitoramentoControl;
    this.preencherFormulario(control ? control.value : null);

    if (this.visualizacao) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => this.sincronizarComConsulta())
    );

    if (control) {
      // Dados carregados após a criação do componente (ex.: GET /consulta/{id}).
      this.subscriptions.push(
        control.valueChanges.subscribe(valor => {
          if (!this.sincronizandoConsulta) {
            this.preencherFormulario(valor);
          }
        })
      );
    }

    this.sincronizarComConsulta();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    const control = this.monitoramentoControl;
    if (!this.visualizacao && control) {
      control.setValue(null, { emitEvent: false });
    }
  }

  /**
   * Método público para que componentes pais possam obter o payload do monitoramento
   * aplicando as validações do formulário.
   */
  getMonitoramentoPayloadIfValid(): MonitoramentoConciliacaoRequest | null {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return null;
    }

    return mapMonitoramentoFormToRequest(this.form.getRawValue());
  }

  campoInvalido(nome: string): boolean {
    const control = this.form.get(nome);
    return !!control && control.invalid && control.touched;
  }

  private preencherFormulario(dados: MonitoramentoConciliacaoRequest | null): void {
    if (!dados) {
      return;
    }

    if (dados.anoReferencia && this.anos.indexOf(dados.anoReferencia) < 0) {
      this.anos = [...this.anos, dados.anoReferencia].sort((a, b) => b - a);
    }

    this.form.patchValue({
      id: dados.id,
      anoReferencia: dados.anoReferencia,
      mesReferencia: dados.mesReferencia,
      quantidadeRequerimentos: dados.quantidadeRequerimentos,
      quantidadeSessoes: dados.quantidadeSessoes,
      valorTotalAcordos: dados.valorTotalAcordos
    }, { emitEvent: false });
  }

  private sincronizarComConsulta(): void {
    const control = this.monitoramentoControl;
    if (!control) {
      return;
    }

    this.sincronizandoConsulta = true;
    control.setValue(mapMonitoramentoFormToRequest(this.form.getRawValue()));
    this.sincronizandoConsulta = false;
  }
}
